import { useCallback, useEffect, useState } from 'react';
import { App, Select, Skeleton, Space, Typography } from 'antd';
import { BellOutlined } from '@ant-design/icons';
import { listOversightAlerts } from '@/api/oversight';
import { isNotAvailable, normalizeApiError } from '@/api/errors';
import type { OversightAlert } from '@/api/types';
import { formatRelative } from '@/lib/time';
import { SeverityTag, alertTypeIcon, alertTypeLabel } from './badges';
import { erasedOr } from '@/lib/erased';

const { Text } = Typography;

const ALERT_TYPES = [
  { value: 'SLA_BREACH', label: 'SLA breach' },
  { value: 'RECONCILIATION_BREAK', label: 'Reconciliation break' },
  { value: 'REVIEW_OVERDUE', label: 'Review overdue' },
];

/**
 * Oversight alerts feed (F-TA-02), filterable by type and spoke. Severity is always colour + glyph +
 * text (deuteranopia-safe). Row → focuses the spoke via `onFocusSpoke`. Degrades to a teaching empty
 * line when the alerts endpoint is not live.
 */
export function AlertsPanel({
  spokeOptions,
  onFocusSpoke,
}: {
  spokeOptions: { value: string; label: string }[];
  onFocusSpoke?: (spokeId: string) => void;
}) {
  const { message } = App.useApp();
  const [type, setType] = useState<string | undefined>();
  const [spokeId, setSpokeId] = useState<string | undefined>();
  const [alerts, setAlerts] = useState<OversightAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);
  const now = Date.now();

  const load = useCallback(async () => {
    setLoading(true);
    setUnavailable(false);
    try {
      const res = await listOversightAlerts({ type, spokeId });
      setAlerts(res.alerts);
    } catch (e) {
      const n = normalizeApiError(e);
      if (isNotAvailable(n)) setUnavailable(true);
      else message.error(n.message);
      setAlerts([]);
    } finally {
      setLoading(false);
    }
    // message stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, spokeId]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div
      style={{
        border: '1px solid var(--ol-border)',
        borderRadius: 10,
        background: 'var(--ol-bg-container)',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
      }}
    >
      <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--ol-border)' }}>
        <div className="ol-section" style={{ margin: 0 }}>
          <BellOutlined aria-hidden />
          Alerts
          <span className="ol-section__count">{unavailable ? '' : alerts.length}</span>
        </div>
        <Space size={8} wrap style={{ marginTop: 10 }}>
          <Select
            allowClear
            size="small"
            placeholder="Type"
            style={{ width: 150 }}
            value={type}
            onChange={setType}
            options={ALERT_TYPES}
          />
          <Select
            allowClear
            size="small"
            showSearch
            placeholder="Spoke"
            style={{ width: 150 }}
            value={spokeId}
            onChange={setSpokeId}
            options={spokeOptions}
            optionFilterProp="label"
          />
        </Space>
      </div>

      <div style={{ overflow: 'auto', padding: 12, flex: 1, minHeight: 0 }}>
        {loading ? (
          <Skeleton active paragraph={{ rows: 4 }} />
        ) : unavailable ? (
          <Text type="secondary" style={{ fontSize: 12 }}>
            The oversight alerts feed lands here once the alerts endpoint is live (Track A). It surfaces
            SLA breaches, reconciliation breaks and overdue reviews across every spoke, severity-ranked.
          </Text>
        ) : alerts.length === 0 ? (
          <Text type="secondary" style={{ fontSize: 12 }}>
            No alerts match the current filters. A clean board is a good board.
          </Text>
        ) : (
          alerts.map((a) => {
            const clickable = Boolean(onFocusSpoke && a.spokeId);
            return (
              <div
                key={a.alertId}
                role={clickable ? 'button' : undefined}
                tabIndex={clickable ? 0 : undefined}
                onClick={clickable ? () => onFocusSpoke!(a.spokeId) : undefined}
                onKeyDown={
                  clickable
                    ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onFocusSpoke!(a.spokeId);
                        }
                      }
                    : undefined
                }
                style={{
                  padding: '9px 11px',
                  border: '1px solid var(--ol-border-subtle)',
                  borderLeft: `3px solid ${severityBorder(a.severity)}`,
                  borderRadius: 8,
                  marginBottom: 8,
                  cursor: clickable ? 'pointer' : 'default',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600 }}>
                    {alertTypeIcon(a.type)}
                    {alertTypeLabel(a.type)}
                  </span>
                  <SeverityTag severity={a.severity} />
                </div>
                <div style={{ marginTop: 4, fontSize: 12, color: 'var(--ol-text-secondary)', lineHeight: 1.4 }}>
                  {erasedOr(a.detail)}
                </div>
                <div style={{ marginTop: 4, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Text className="ol-mono" style={{ fontSize: 10.5, color: 'var(--ol-text-tertiary)' }}>
                    {a.spokeId}
                  </Text>
                  <Text style={{ fontSize: 10.5, color: 'var(--ol-text-tertiary)' }} title={a.at}>
                    {formatRelative(a.at, now)}
                  </Text>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

function severityBorder(severity: string): string {
  const s = severity?.toUpperCase?.();
  if (s === 'CRITICAL' || s === 'HIGH') return 'var(--ol-negative)';
  if (s === 'MEDIUM' || s === 'MED') return 'var(--ol-attention)';
  return 'var(--ol-info)';
}
